import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
// Subpath entry (not the main barrel) so the server bundle
// (`actor-database.server.ts` → this file) doesn't drag in the platform sqlite
// machinery — see the EAS Hosting gotcha in that file.
import { runWithBufferedDatabaseChanges } from "@-/com.npmjs.expo-sqlite/database-change-transaction-buffer";
// Subpath entry for the same reason as the buffer import above: `busy-retry` is a
// dependency-free module, so the server bundle doesn't pull the engine barrel's
// expo-sqlite/Turso machinery in behind it.
import { retryOnDatabaseLocked } from "@-/dev.improve.haniverse.lib.database.engine/busy-retry";
// Same dependency-free-subpath reasoning as `busy-retry` above.
import { retryOnLeaderChange } from "@-/dev.improve.haniverse.lib.database.engine/leader-replay";
import {
  bindDatabaseCrudMethods,
  isSoftDeletableTable,
  softDeleteTimestamp,
  wrapRelationalQuery,
} from "@-/dev.improve.haniverse.database._lib.wrap-database";
import { isAnyObject } from "@-/com.npmjs.is-what";
import onetime from "@-/com.npmjs.onetime";
import { objectKeys } from "@-/com.npmjs.ts-extras";
import type {
  ActorDatabaseHandle,
  ActorRawDatabaseHandle,
  ActorRawTransaction,
  ActorTransaction,
} from "#types";

/** `contentTable` overlays (e.g. `userGraphemeMeaningCard`) have no `deletedAt` column. */
const getActorSoftDeleteTableNames = onetime(
  (): ReadonlySet<string> =>
    new Set(
      objectKeys(actorSchema).filter((tableName) => isSoftDeletableTable(actorSchema[tableName])),
    ),
);

function isActorRawQuery(value: unknown): value is ActorRawDatabaseHandle["query"] {
  return isAnyObject(value);
}

// Wraps `raw.query` so `findMany` / `findFirst` on `crudTable` rows inject the
// soft-delete predicate at the top level. `contentTable` overlays are unchanged.
// Nested `with: { ... }` clauses are filtered by `where` on `actorRelations`.
function wrapQuery(rawQuery: ActorRawDatabaseHandle["query"]): ActorRawDatabaseHandle["query"] {
  return wrapRelationalQuery(rawQuery, {
    assertQuery: isActorRawQuery,
    proxyBuildErrorMessage: "Failed to build actor query proxy",
    shouldApplySoftDelete: (tableName) => getActorSoftDeleteTableNames().has(tableName),
  });
}

// `.transaction(cb)` always calls `raw.transaction(...)`. The local-tursodb
// "no real transaction" behavior the seed route needs now lives one layer down,
// in the `drizzleLocal` session (`getServerActorDatabase` picks it by URL): that
// session runs `.transaction(...)` without the explicit `BEGIN` local tursodb
// rejects, so the wrapper doesn't need a transactionless mode of its own.
export function wrapActorDatabase(raw: ActorRawDatabaseHandle): ActorDatabaseHandle {
  return {
    ...bindDatabaseCrudMethods(raw),
    query: wrapQuery(raw.query),
    softDelete: (table) => ({
      where: (condition) => raw.update(table).set(softDeleteTimestamp()).where(condition),
    }),
    // Buffer change-event delivery until the transaction settles: SQLite's
    // update hook fires per statement (pre-commit), and each awaited statement
    // is its own microtask window, so without the buffer one transaction
    // produced several invalidation waves whose refetches read half-written
    // state and congested the serialized SQLite queue. `raw.transaction`
    // resolves only after COMMIT/ROLLBACK, so the flush is post-commit.
    // Nested savepoint transactions (`wrapActorTransaction`'s `transaction`)
    // always run inside this window and need no wrapping of their own.
    //
    // `retryOnDatabaseLocked` closes the one hole the connection-level
    // `withSqliteBusyRetry` deliberately leaves open: it will not spin on a
    // statement in the MIDDLE of an explicit transaction (that would hold the
    // transaction's locks and can deadlock against the very connection we're
    // waiting on). SQLite's answer there is to let the transaction roll back and
    // replay it whole, which is what this does — the callback is pure DB work, so
    // re-running it is safe. Without it, a lock collision inside a write
    // transaction would still escape to the UI as "database is locked".
    //
    // `retryOnLeaderChange` (web multi-tab, plan §2.7) applies the same "replay
    // it whole" answer to a transaction interrupted by a leader handover. A
    // transaction that never reached `commit` had no effect, so re-running it
    // duplicates nothing; the proxied connection stamps the one exception — a
    // `commit` lost mid-flight, which may have landed — as not replayable, and
    // that case still surfaces to the caller. Outermost, so a replayed
    // transaction gets a fresh lock-retry budget of its own. Inert on native and
    // on the server, where a `LeaderChangedError` can never occur.
    transaction: (callback) =>
      retryOnLeaderChange(() =>
        retryOnDatabaseLocked(() =>
          runWithBufferedDatabaseChanges(() =>
            raw.transaction(async (rawTx) => callback(wrapActorTransaction(rawTx))),
          ),
        ),
      ),
  };
}

function wrapActorTransaction(raw: ActorRawTransaction): ActorTransaction {
  return {
    ...bindDatabaseCrudMethods(raw),
    query: wrapQuery(raw.query),
    queryRaw: raw.query,
    softDelete: (table) => ({
      where: (condition) => raw.update(table).set(softDeleteTimestamp()).where(condition),
    }),
    transaction: (callback) =>
      raw.transaction(async (rawTx) => callback(wrapActorTransaction(rawTx))),
  };
}
